import {
  createRootRoute,
  createRoute,
  createRouter,
  Navigate,
} from "@tanstack/react-router";
import { Shell } from "./routes/Shell";
import { Home } from "./routes/Home";
import { Request } from "./routes/Request";
import { Requests } from "./routes/Requests";
import { Approvals } from "./routes/Approvals";
import { Receive } from "./routes/Receive";
import { Dispatch } from "./routes/Dispatch";
import { Invoices } from "./routes/Invoices";
import { Statements } from "./routes/Statements";
import { Pricing } from "./routes/Pricing";
import { Clinics } from "./routes/Clinics";
import { Reports } from "./routes/Reports";
import { Inventory } from "./routes/Inventory";
import { Count } from "./routes/Count";
import { Compliance } from "./routes/Compliance";
import { Levels } from "./routes/Levels";
import { Alerts } from "./routes/Alerts";
import { Users } from "./routes/Users";
import { ErrorState, cleanConvexError } from "./components/states";

/** The app's route map: one path per screen, plus a catch-all back to `/`. */

/**
 * The last line of defence.
 *
 * A thrown query must never produce a white screen with "Something went wrong"
 * on a projector. This names what failed and offers the one button worth
 * pressing, and it is wired at the root so it catches every screen — including
 * ones no one has written yet.
 */
function RootErrorBoundary({ error }: { error: Error }) {
  return (
    <div className="mx-auto max-w-md p-6">
      <ErrorState
        title="This screen could not load"
        body={cleanConvexError(error, 300)}
        onRetry={() => window.location.reload()}
      />
    </div>
  );
}

const rootRoute = createRootRoute({
  component: Shell,
  errorComponent: RootErrorBoundary,
});

/** No placeholder routes left: every route in the nav renders a real screen. */

/**
 * Every route gets the boundary, not just the root.
 *
 * Caught at the root, a thrown screen takes the header and the bottom nav down
 * with it and leaves whoever is watching with one button: reload. Caught here,
 * the chrome survives and the next tap is a different screen rather than a
 * restart — which on a projector is the difference between a bad moment and a
 * dead demo. Screens with their own `ScreenBoundary` still catch first and keep
 * their retry-in-place; this is the floor under the rest — including `/`, which
 * was the one route assembled by hand and so the one screen with no boundary
 * of its own at all.
 */
const built = (path: string, component: () => React.ReactElement) =>
  createRoute({
    getParentRoute: () => rootRoute,
    path,
    component,
    errorComponent: RootErrorBoundary,
  });

const routes = [
  built("/", Home),

  built("/request", Request),
  built("/requests", Requests),
  built("/count", Count),
  built("/compliance", Compliance),
  built("/levels", Levels),
  built("/approvals", Approvals),
  built("/receive", Receive),
  built("/dispatch", Dispatch),
  built("/invoices", Invoices),
  built("/statements", Statements),
  built("/pricing", Pricing),

  built("/inventory", Inventory),
  built("/reports", Reports),
  built("/alerts", Alerts),
  built("/settings/clinics", Clinics),
  built("/settings/users", Users),
];

const catchAll = createRoute({
  getParentRoute: () => rootRoute,
  path: "$",
  component: () => <Navigate to="/" replace />,
});

export const router = createRouter({
  routeTree: rootRoute.addChildren([...routes, catchAll]),
  defaultPreload: "intent",
  scrollRestoration: true,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
